import type {
  CustomFieldDefinition,
  CustomFieldDefinitionCreate,
  CustomFieldType,
} from '@erp/shared';
import { Ban, Pencil, Plus, RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const FIELD_TYPES: CustomFieldType[] = [
  'text',
  'longtext',
  'number',
  'select',
  'multiselect',
  'boolean',
  'date',
];
const OPTION_TYPES = new Set<CustomFieldType>(['select', 'multiselect']);

/** Derive a safe lower_snake_case field code from a human label. */
function slugifyCode(label: string): string {
  const base = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_')
    .slice(0, 64);
  if (!base) return '';
  return /^[a-z]/.test(base) ? base : `f_${base}`.slice(0, 64);
}

export function CustomFieldsPage(): JSX.Element {
  const { t } = useTranslation('customFields');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const [defs, setDefs] = useState<CustomFieldDefinition[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: CustomFieldDefinition[] }>('/custom-fields');
    setDefs(r.data.items);
  };
  useEffect(() => {
    void reload();
  }, []);

  const editing = editingId && defs ? defs.find((d) => d.id === editingId) ?? null : null;

  const onDeactivate = async (d: CustomFieldDefinition): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmDeactivate', { label: d.label }), tone: 'danger' }))) return;
    try {
      await api().delete(`/custom-fields/${d.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };
  const onReactivate = async (d: CustomFieldDefinition): Promise<void> => {
    try {
      await api().patch(`/custom-fields/${d.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<CustomFieldDefinition>[] = [
    {
      id: 'label',
      header: t('columns.label'),
      sortValue: (d) => d.label,
      searchValue: (d) => d.label,
      cellClassName: 'font-medium',
      cell: (d) => d.label,
    },
    {
      id: 'code',
      header: t('columns.code'),
      sortValue: (d) => d.code,
      searchValue: (d) => d.code,
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (d) => d.code,
    },
    {
      id: 'type',
      header: t('columns.type'),
      sortValue: (d) => d.fieldType,
      cell: (d) => t(`type.${d.fieldType}`),
    },
    {
      id: 'options',
      header: t('columns.options'),
      cell: (d) =>
        OPTION_TYPES.has(d.fieldType) && d.options && d.options.length > 0 ? (
          <span className="flex flex-wrap gap-1">
            {d.options.slice(0, 4).map((o) => (
              <Badge key={o} variant="secondary">
                {o}
              </Badge>
            ))}
            {d.options.length > 4 && (
              <span className="text-xs text-fg-muted">+{d.options.length - 4}</span>
            )}
          </span>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
    {
      id: 'visibility',
      header: t('columns.visibility'),
      cell: (d) => {
        const pills: string[] = [];
        if (d.isRequired) pills.push(t('visibility.required'));
        if (d.showInList) pills.push(t('visibility.list'));
        // Only flagged when OFF: visible-to-customers is the default, so a pill on
        // every row would say nothing. The absence of one is the exception.
        if (!d.showPublic) pills.push(t('visibility.internal'));
        if (d.filterable) pills.push(t('visibility.filter'));
        if (pills.length === 0) return <span className="text-fg-subtle">{t('visibility.none')}</span>;
        return (
          <span className="flex flex-wrap gap-1">
            {pills.map((p) => (
              <Badge key={p} variant="outline">
                {p}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: t('columns.status'),
      sortValue: (d) => (d.isActive ? 1 : 0),
      cell: (d) => (
        <Badge variant={d.isActive ? 'success' : 'secondary'}>
          {t(`status.${d.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
  ];
  if (canWrite) {
    columns.push({
      id: 'actions',
      header: t('columns.actions'),
      align: 'end',
      cell: (d) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="cf-act"
            onClick={() => {
              setShowCreate(false);
              setEditingId(d.id);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            aria-label={`${tc('actions.edit')} ${d.label}`}
            title={tc('actions.edit')}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
          {d.isActive ? (
            <button
              type="button"
              className="cf-act"
              onClick={() => void onDeactivate(d)}
              aria-label={`${t('actions.deactivate')} ${d.label}`}
              title={t('actions.deactivate')}
            >
              <Ban className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              className="cf-act"
              onClick={() => void onReactivate(d)}
              aria-label={`${t('actions.reactivate')} ${d.label}`}
              title={t('actions.reactivate')}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      ),
    });
  }

  if (!defs) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <style>{CF_CSS}</style>
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('description')}</p>
        </div>
        {canWrite && !showCreate && !editingId && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('addField')}
          </Button>
        )}
      </header>

      {showCreate && (
        <CustomFieldForm
          onCancel={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}
      {editing && (
        <CustomFieldForm
          initial={editing}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            setEditingId(null);
            await reload();
          }}
        />
      )}

      {defs.length === 0 && !showCreate ? (
        <EmptyState
          icon={<SlidersHorizontal className="h-8 w-8" aria-hidden />}
          title={t('empty')}
          description={t('emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('addField')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={defs}
            rowKey={(d) => d.id}
            searchPlaceholder={t('searchPlaceholder')}
            rowClassName={(d) => (d.isActive ? undefined : 'opacity-60')}
          />
        </Card>
      )}
    </div>
  );
}

interface FormState {
  label: string;
  code: string;
  fieldType: CustomFieldType;
  options: string[];
  isRequired: boolean;
  showInList: boolean;
  showPublic: boolean;
  filterable: boolean;
  sortOrder: number;
}

interface CustomFieldFormProps {
  initial?: CustomFieldDefinition;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function CustomFieldForm({ initial, onCancel, onSaved }: CustomFieldFormProps): JSX.Element {
  const { t } = useTranslation('customFields');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const [form, setForm] = useState<FormState>(() =>
    initial
      ? {
          label: initial.label,
          code: initial.code,
          fieldType: initial.fieldType,
          options: initial.options ?? [],
          isRequired: initial.isRequired,
          showInList: initial.showInList,
          showPublic: initial.showPublic,
          filterable: initial.filterable,
          sortOrder: initial.sortOrder,
        }
      : {
          label: '',
          code: '',
          fieldType: 'text',
          options: [],
          isRequired: false,
          showInList: true,
          showPublic: true,
          filterable: false,
          sortOrder: 0,
        },
  );
  // On create, the code auto-follows the label until the user edits it.
  const [codeTouched, setCodeTouched] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isOptionType = OPTION_TYPES.has(form.fieldType);

  const onLabelChange = (label: string): void =>
    setForm((f) => ({ ...f, label, code: codeTouched ? f.code : slugifyCode(label) }));

  const setOption = (i: number, val: string): void =>
    setForm((f) => ({ ...f, options: f.options.map((o, idx) => (idx === i ? val : o)) }));
  const addOption = (): void => setForm((f) => ({ ...f, options: [...f.options, ''] }));
  const removeOption = (i: number): void =>
    setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const cleanOptions = form.options.map((o) => o.trim()).filter(Boolean);
    if (isOptionType && cleanOptions.length === 0) {
      setError(t('errors.optionsRequired'));
      return;
    }
    setSaving(true);
    try {
      if (isEdit && initial) {
        await api().patch(`/custom-fields/${initial.id}`, {
          label: form.label.trim(),
          options: isOptionType ? cleanOptions : null,
          isRequired: form.isRequired,
          showInList: form.showInList,
          showPublic: form.showPublic,
          filterable: form.filterable,
          sortOrder: Number(form.sortOrder) || 0,
        });
      } else {
        const payload: CustomFieldDefinitionCreate = {
          code: form.code.trim(),
          label: form.label.trim(),
          fieldType: form.fieldType,
          isRequired: form.isRequired,
          showInList: form.showInList,
          filterable: form.filterable,
          sortOrder: Number(form.sortOrder) || 0,
        };
        if (isOptionType) payload.options = cleanOptions;
        await api().post('/custom-fields', payload);
      }
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'conflict') setError(t('errors.duplicateCode'));
      else if (code === 'validation_failed') setError(t('errors.badCode'));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {isEdit ? t('form.editTitle', { label: initial?.label }) : t('form.createTitle')}
      </h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="cf-label" label={t('form.label')} helperText={t('form.labelHint')} required>
          <Input
            id="cf-label"
            value={form.label}
            onChange={(e) => onLabelChange(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField id="cf-code" label={t('form.code')} helperText={t('form.codeHint')} required>
          <Input
            id="cf-code"
            value={form.code}
            onChange={(e) => {
              setCodeTouched(true);
              setForm({ ...form, code: e.target.value });
            }}
            required
            readOnly={isEdit}
            aria-readonly={isEdit}
            className={isEdit ? 'cursor-not-allowed bg-bg-muted text-fg-muted' : undefined}
          />
        </FormField>
        <FormField
          id="cf-type"
          label={t('form.type')}
          helperText={isEdit ? t('form.typeHint') : undefined}
          required
        >
          <select
            id="cf-type"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg disabled:cursor-not-allowed disabled:bg-bg-muted disabled:text-fg-muted"
            value={form.fieldType}
            onChange={(e) => setForm({ ...form, fieldType: e.target.value as CustomFieldType })}
            disabled={isEdit}
          >
            {FIELD_TYPES.map((ft) => (
              <option key={ft} value={ft}>
                {t(`type.${ft}`)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="cf-sort" label={t('form.sortOrder')}>
          <Input
            id="cf-sort"
            type="number"
            value={String(form.sortOrder)}
            onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
          />
        </FormField>

        {isOptionType && (
          <div className="md:col-span-2">
            <div className="text-sm font-medium text-fg">{t('form.options')}</div>
            <p className="mt-0.5 text-xs text-fg-muted">{t('form.optionsHint')}</p>
            <div className="mt-2 flex flex-col gap-2">
              {form.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={o}
                    onChange={(e) => setOption(i, e.target.value)}
                    placeholder={t('form.optionPlaceholder')}
                    aria-label={`${t('form.optionPlaceholder')} ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="cf-act"
                    onClick={() => removeOption(i)}
                    aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                    title={tc('actions.remove', { defaultValue: 'Remove' })}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              ))}
              <div>
                <Button type="button" variant="secondary" size="sm" onClick={addOption}>
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('form.addOption')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="md:col-span-2 flex flex-wrap gap-x-6 gap-y-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={(e) => setForm({ ...form, isRequired: e.target.checked })}
            />
            <span>{t('form.required')}</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.showInList}
              onChange={(e) => setForm({ ...form, showInList: e.target.checked })}
            />
            <span>{t('form.showInList')}</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.showPublic}
              onChange={(e) => setForm({ ...form, showPublic: e.target.checked })}
            />
            <span>{t('form.showPublic')}</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.filterable}
              onChange={(e) => setForm({ ...form, filterable: e.target.checked })}
            />
            <span>{t('form.filterable')}</span>
          </label>
        </div>

        {error && (
          <p className="md:col-span-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>
            {isEdit ? tc('actions.save') : t('form.submit')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const CF_CSS = `
.cf-act { width: 28px; height: 28px; border-radius: 6px; display: inline-grid; place-items: center; color: var(--text-subtle); border: 0; background: transparent; cursor: pointer; }
.cf-act:hover { background: var(--surface-2); color: var(--text); }
`;
